import { useTranslation } from "@/language"
/**
 * ExploreMoreSection Component
 * Displays related rituals on the ritual detail page
 * Shows up to 5 rituals excluding the current one using the explore rituals API
 */

import { ArrowRight } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import type { RitualCardProps } from "@/components/cards/ritualCard"
import { RitualCard } from "@/components/cards/ritualCard"
import { Carousel } from "@/components/carousel/Carousel"
import { useExploreRituals } from "@/features/learn-practice/hooks/useExploreRituals"
import { useToggleSaveRitual } from "@/features/learn-practice/hooks/useToggleSaveRitual"
import { RITUAL_FORMAT_LABELS } from "@/features/learn-practice/types"
import { formatDuration } from "../../utils/formatDuration"

export interface ExploreMoreSectionProps {
  currentRitualId: string
}

export function ExploreMoreSection({ currentRitualId }: ExploreMoreSectionProps) {
  const navigate = useNavigate()
  const { t } = useTranslation("rituals")
  const location = useLocation()
  const toggleSave = useToggleSaveRitual()

  const handleShowMore = () => {
    if (location.pathname === "/learn-and-practice") {
      document
        .getElementById("explore-self-guided-rituals-listing")
        ?.scrollIntoView({ behavior: "smooth" })
    } else {
      navigate("/learn-and-practice", {
        state: { scrollTo: "explore-self-guided-rituals-listing" },
      })
    }
  }

  // Fetch explore rituals - we'll filter out the current one
  const { rituals: exploreRituals, isLoading, error } = useExploreRituals({})

  // Filter out current ritual and limit to 5
  const relatedRituals = exploreRituals.filter(ritual => ritual.id !== currentRitualId).slice(0, 5)

  // Don't render if still loading first batch
  if (exploreRituals.length === 0 && isLoading) {
    return null
  }

  // Don't render if no related rituals after filtering, unless we need to show an error state
  if (relatedRituals.length === 0 && !error) {
    return null
  }

  // Map ExploreRitual to RitualCardProps
  const cardData: RitualCardProps[] = relatedRituals.map(r => {
    const formatLabel = RITUAL_FORMAT_LABELS[r.ritualType] ?? r.ritualType

    return {
      id: r.id,
      image: r.coverImage ?? undefined,
      userAvatar: r.user?.profilePictureUrl ?? undefined,
      username: r.user?.name ?? "Unknown",
      userHandle: r.user?.username ? `@${r.user?.username}` : "",
      title: r.title,
      tags: [formatLabel],
      duration: r.duration ? formatDuration(r.duration) : "",
      isBookmarked: r.isSaved,
      onBookmarkClick: () => toggleSave.mutate(r.id),
      userId: r.user?.id,
      price: r.fee ?? undefined,
      participationFee: r.isDonationBased ? "Donation-based" : r.isPaid ? "Paid" : "Free",
      ritualTs: r.ritualTs,
      city: r.city,
      state: r.state,
    }
  })

  return (
    <section className="w-full max-w-360 mx-auto px-5 md:px-17.5 py-10 md:py-17.5 flex flex-col gap-5.5">
      <div className="flex items-center justify-between">
        <h2 className="text-[18px] leading-7.5 text-primary uppercase font-semibold">
          {t("exploreMore.title")}
        </h2>
        <button
          onClick={handleShowMore}
          className="flex items-center gap-1 text-[14px] text-primary/60 hover:text-primary transition-colors"
        >
          {t("exploreMore.showMore")}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {error ? (
        <div className="flex items-center justify-center h-[200px] text-primary/60">
          <p>{t("exploreMore.failedToLoad")}</p>
        </div>
      ) : (
        <Carousel
          data={cardData}
          renderItem={card => (
            <div className="h-[260px] md:h-[440px] max-w-[640px]">
              <RitualCard {...card} />
            </div>
          )}
          itemsPerScreen={{ sm: 1, md: 2, lg: 2, xl: 2 }}
          loop={false}
        />
      )}
    </section>
  )
}
